'use client';

import { ForwardRefRenderFunction, ReactNode, useEffect, useImperativeHandle, useRef } from 'react';
import { forwardRef } from 'react';
import { Root as RadixRoot } from '@radix-ui/react-checkbox';
import { Checkbox, CheckboxProps } from '../Checkbox';
import { CheckboxFieldLabel } from './CheckboxFieldLabel';
import { CheckboxFieldNote } from './CheckboxFieldNote';
import { CheckboxFieldError } from './CheckboxFieldError';
import * as RadixForm from '@radix-ui/react-form';
import { InputFieldError } from '../InputField/InputFieldError';
import { twMerge } from 'tailwind-merge';
export type CheckboxFieldProps = Omit<CheckboxProps, 'required'> & {
  label?: ReactNode;
  note?: ReactNode;
  error?: string;
  asInline?: boolean;

  /**
   * Use `label` if you don't want to add the `required` attribute to the input element itself.
   * This will avoid highlighting the input as invalid, when there is no value.
   * This is usually a user friendlier way than highlighting the field invalid on initial page load.
   */
  required?: boolean | 'label';
  asRadix?: boolean;
  radixFormMessages?: RadixForm.FormMessageProps[];
};
const CheckboxFieldRenderFunction: ForwardRefRenderFunction<HTMLInputElement | null, CheckboxFieldProps> = ({
  label,
  note,
  error,
  required,
  asRadix,
  radixFormMessages,
  asInline,
  ...attrs
}, forwardedRef) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(forwardedRef, () => inputRef.current);
  useEffect(() => {
    if (asRadix) {
      return;
    }
    inputRef.current?.setCustomValidity(error ?? '');
  }, [asRadix, error]);
  if (!asRadix) {
    return <div className="space-y-1">
        <label className={twMerge('flex cursor-pointer gap-2', asInline && 'inline-flex')}>
          <Checkbox {...attrs} required={typeof required === 'boolean' ? required : undefined} ref={inputRef} />

          {(label || note) && <div>
              {label && <CheckboxFieldLabel asChild>
                  <div>
                    {label}
                    {required ? '*' : ''}
                  </div>
                </CheckboxFieldLabel>}
              {note && <CheckboxFieldNote>
                  {note}
                  {!label && required ? '*' : ''}
                </CheckboxFieldNote>}
            </div>}
        </label>

        {error && <CheckboxFieldError>{error}</CheckboxFieldError>}
      </div>;
  }
  return <RadixForm.Field name={attrs.name ?? ''} asChild data-sentry-element="RadixForm.Field" data-sentry-component="CheckboxFieldRenderFunction" data-sentry-source-file="CheckboxField.tsx">
      <div className="space-y-1">
        <label className="flex cursor-pointer gap-2">
          <RadixForm.Control asChild data-sentry-element="RadixForm.Control" data-sentry-source-file="CheckboxField.tsx">
            <RadixRoot asChild data-sentry-element="RadixRoot" data-sentry-source-file="CheckboxField.tsx">
              <Checkbox {...attrs} required={typeof required === 'boolean' ? required : undefined} data-sentry-element="Checkbox" data-sentry-source-file="CheckboxField.tsx" />
            </RadixRoot>
          </RadixForm.Control>

          {(label || note) && <div>
              {label && <RadixForm.Label asChild>
                  <CheckboxFieldLabel asChild>
                    <div>
                      {label}
                      {required ? '*' : ''}
                    </div>
                  </CheckboxFieldLabel>
                </RadixForm.Label>}
              {note && <CheckboxFieldNote>
                  {note}
                  {!label && required ? '*' : ''}
                </CheckboxFieldNote>}
            </div>}
        </label>

        {(radixFormMessages ?? []).map((props, idx) => <RadixForm.Message key={idx} {...props} asChild>
            <InputFieldError>{props.children}</InputFieldError>
          </RadixForm.Message>)}
      </div>
    </RadixForm.Field>;
};
export const CheckboxField = forwardRef<HTMLInputElement | null, CheckboxFieldProps>(CheckboxFieldRenderFunction);